import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useEmployeeViewSettings } from "@/hooks/useEmployeeViewSettings";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RestrictedPageNotice } from "@/components/RestrictedPageNotice";
import { ActivityTimeline } from "@/components/ActivityTimeline";
import { buildActivityEntries, getRangeQueryBounds, summarizeActivity, type ActivityEntry, type ActivityRange } from "@/lib/activity";
import { useToast } from "@/hooks/use-toast";
import { UserAvatar } from "@/components/UserAvatar";
import { formatInZambia, ZAMBIA_TIMEZONE } from "@/lib/zambia-time";

function getRangeLabel(range: ActivityRange) {
  if (range === "today") return "today";
  if (range === "week") return "this week";
  if (range === "month") return "this month";
  return "all time";
}

type SaleRow = {
  id: string;
  item_name: string | null;
  quantity: number | null;
  final_amount: number | null;
  total_price: number | null;
  created_at: string | null;
  sold_by: string | null;
  voided_at: string | null;
};

type ExpenseRow = {
  id: string;
  category: string | null;
  description: string | null;
  amount: number;
  expense_date: string;
  date: string | null;
  created_by: string | null;
};

function getGreeting(): string {
  const hour = parseInt(
    new Intl.DateTimeFormat("en", { hour: "numeric", hour12: false, timeZone: ZAMBIA_TIMEZONE }).format(new Date()),
    10
  );
  if (hour >= 5 && hour < 12) return "Good morning";
  if (hour >= 12 && hour < 17) return "Good afternoon";
  return "Good evening";
}

export default function MyActivityPage() {
  const { user, businessId, role, profile } = useAuth();
  const { settings } = useEmployeeViewSettings();
  const { toast } = useToast();
  const [range, setRange] = useState<ActivityRange>("today");
  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingSaleId, setDeletingSaleId] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.id) return;
    const fetchActivity = async () => {
      setLoading(true);
      const bounds = getRangeQueryBounds(range);

      let salesQuery = supabase
        .from("sales")
        .select("id, item_name, quantity, final_amount, total_price, created_at, sold_by, voided_at")
        .eq("sold_by", user.id)
        .is("voided_at", null)
        .order("created_at", { ascending: false });

      let expensesQuery = supabase
        .from("expenses")
        .select("id, category, description, amount, expense_date, date, created_by")
        .eq("created_by", user.id)
        .order("date", { ascending: false });

      if (bounds) {
        salesQuery = salesQuery
          .gte("created_at", bounds.startIso)
          .lte("created_at", bounds.endIso);
        expensesQuery = expensesQuery
          .gte("expense_date", bounds.startDate)
          .lte("expense_date", bounds.endDate);
      }

      salesQuery = salesQuery.eq("business_id", businessId);
      expensesQuery = expensesQuery.eq("business_id", businessId);

      const [salesRes, expensesRes] = await Promise.all([salesQuery, expensesQuery]);
      setEntries(
        buildActivityEntries(
          (salesRes.data ?? []) as SaleRow[],
          (expensesRes.data ?? []) as ExpenseRow[],
          {},
          {
            allowDeleteSales: settings.allowEmployeeDeleteSale,
            saleOwnerId: user.id,
          },
        ),
      );
      setLoading(false);
    };

    fetchActivity();
  }, [businessId, range, settings.allowEmployeeDeleteSale, user?.id]);

  if (role === "employee" && !settings.allowEmployeeViewSalesHistory) {
    return (
      <RestrictedPageNotice
        title="My Activity is disabled"
        description="Your manager has turned off activity-history access for employee accounts."
      />
    );
  }

  const summary = summarizeActivity(entries);

  const handleDeleteSale = async (saleId: string) => {
    if (!user?.id) return;
    setDeletingSaleId(saleId);
    const { error } = await supabase
      .from("sales")
      .update({ voided_at: new Date().toISOString(), voided_note: "Voided by employee within 24 hours" })
      .eq("id", saleId)
      .eq("sold_by", user.id)
      .is("voided_at", null);
    setDeletingSaleId(null);

    if (error) {
      toast({ title: "Could not delete sale", description: error.message, variant: "destructive" });
      return;
    }

    setEntries((current) => current.filter((entry) => entry.id !== saleId));
    toast({ title: "Sale removed", description: "The sale has been removed from active activity." });
  };

  const displayName = profile?.full_name ?? user?.email ?? "there";
  const greetingDate = formatInZambia(new Date(), { dateStyle: "full" });

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <UserAvatar size="md" userId={user?.id} avatarUrl={profile?.avatar_url} fullName={profile?.full_name} />
        <div>
          <h2 className="text-xl font-display font-semibold">
            {getGreeting()}, {displayName}
          </h2>
          <p className="text-sm text-muted-foreground">{greetingDate}</p>
        </div>
      </div>
      <div>
        <h2 className="text-2xl font-display font-bold">My Activity</h2>
        <p className="text-sm text-muted-foreground">
          Browse your full personal sales and expense history by day, week, month, or all time.
        </p>
      </div>

      <Tabs value={range} onValueChange={(value) => setRange(value as ActivityRange)}>
        <TabsList>
          <TabsTrigger value="today">Today</TabsTrigger>
          <TabsTrigger value="week">This Week</TabsTrigger>
          <TabsTrigger value="month">This Month</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="text-sm">Sales</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.salesCount}</div>
          </CardContent>
        </Card>
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="text-sm">My Sales Total</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">K {summary.salesTotal.toLocaleString()}</div>
          </CardContent>
        </Card>
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="text-sm">Expenses</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.expenseCount}</div>
          </CardContent>
        </Card>
      </div>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle className="text-base">Full Activity Timeline</CardTitle>
          <p className="text-sm text-muted-foreground">
            Only your own sales and expenses appear here for {getRangeLabel(range)}. Sales older than 24 hours are read-only.
          </p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-sm text-muted-foreground">Loading activity...</div>
          ) : (
            <ActivityTimeline
              entries={entries}
              emptyText="No activity in this range yet."
              onDeleteSale={settings.allowEmployeeDeleteSale ? handleDeleteSale : undefined}
              deletingSaleId={deletingSaleId}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
